import { formatDoj, tenureOf } from "@/lib/tenure";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Tree } from "react-arborist";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
    ChevronRight as ChevronRightIcon,
    ChevronDown as ChevronDownIcon,
    Network,
    Search as SearchIcon,
    GripVertical,
    User as UserIcon,
    ChevronsDownUp,
    ChevronsUpDown,
    Info,
    ArrowUp,
    List as ListIcon,
} from "lucide-react";
import { toast } from "sonner";
import Skeleton from "@/components/Skeleton";
import OrgTreeNode from "@/components/OrgTreeNode";

const initials = (name = "") =>
    name
        .split(" ")
        .map((x) => x[0])
        .filter(Boolean)
        .slice(0, 2)
        .join("")
        .toUpperCase();

// Distinct deterministic accent per node — keeps the tree readable at a glance.
const accentFromId = (id = "") => {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
    const palette = [
        "bg-teal-100 text-[#1F7F79]",
        "bg-indigo-100 text-indigo-700",
        "bg-amber-100 text-amber-700",
        "bg-rose-100 text-rose-700",
        "bg-violet-100 text-violet-700",
        "bg-sky-100 text-sky-700",
        "bg-emerald-100 text-emerald-700",
        "bg-fuchsia-100 text-fuchsia-700",
    ];
    return palette[h % palette.length];
};

const humanRole = (r, hasChildren = false) => {
    // Indirect manager: role==="employee" but has at least one direct
    // report → display as "Lead" so leads aren't undersold as "Member".
    if (r === "employee" && hasChildren) return "Lead";
    return ({
        super_admin: "Super Admin",
        manager: "Manager",
        coordinator: "Coordinator",
        employee: "Member",
    }[r] || r);
};

const Node = ({ node, style, dragHandle, tree, canEdit, promoteToRoot, currentUserId }) => {
    const navigate = useNavigate();
    const data = node.data;
    const hasChildren = (data.children?.length || 0) > 0;
    // Position takes priority. Falls back to a humanised role label only when
    // no position is set, so we never show two chips that mean the same thing.
    const titleText = data.designation || data.position || humanRole(data.role, hasChildren);
    const isAdmin = data.role === "super_admin";
    const isManager = data.role === "manager";
    const isMe = data.user_id === currentUserId;

    return (
        <div
            ref={dragHandle}
            style={style}
            data-testid={`org-node-${data.user_id}`}
            className={`group flex items-center gap-3 pr-3 rounded-lg transition-colors ${
                isMe
                    ? "bg-[#2EB4AC]/10 ring-1 ring-[#2EB4AC]/40 org-node-me"
                    : node.isSelected ? "bg-teal-50/70 ring-1 ring-[#2EB4AC]" : "hover:bg-slate-50/80"
            } ${node.willReceiveDrop ? "ring-2 ring-indigo-400 bg-indigo-50/40" : ""}`}
            title={data.email || ""}
        >
            {/* Drag handle — only visible on hover for super admins */}
            {canEdit ? (
                <span
                    className="h-9 w-4 flex items-center justify-center text-slate-300 opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing"
                    title="Drag to re-parent"
                    aria-label="Drag handle"
                >
                    <GripVertical className="h-3.5 w-3.5" />
                </span>
            ) : (
                <span className="w-1" />
            )}

            {/* Expand / collapse arrow */}
            <button
                type="button"
                onClick={(e) => {
                    e.stopPropagation();
                    if (hasChildren) node.toggle();
                }}
                className={`h-6 w-6 flex items-center justify-center rounded transition-colors ${
                    hasChildren ? "text-slate-500 hover:bg-slate-100" : "text-transparent pointer-events-none"
                }`}
                aria-label={node.isOpen ? "Collapse" : "Expand"}
                data-testid={hasChildren ? `org-toggle-${data.user_id}` : undefined}
            >
                {hasChildren ? (
                    node.isOpen ? <ChevronDownIcon className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />
                ) : (
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-200" />
                )}
            </button>

            {/* Avatar */}
            <div
                className={`h-9 w-9 rounded-full flex items-center justify-center font-heading text-xs font-semibold shrink-0 ${accentFromId(data.user_id)} ${
                    isAdmin ? "ring-2 ring-offset-2 ring-slate-900/80" : isManager ? "ring-2 ring-offset-2 ring-[#2EB4AC]" : ""
                }`}
            >
                {initials(data.name)}
            </div>

            {/* Name + single title chip */}
            <button
                type="button"
                onClick={() => navigate(`/employees/${data.user_id}`)}
                className="flex-1 min-w-0 text-left py-2 pr-2 flex items-center gap-2"
            >
                <span className="font-medium text-slate-900 truncate text-sm">{data.name}</span>
                {isMe && (
                    <span className="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-bold tracking-wider bg-[#2EB4AC] text-white shrink-0">
                        YOU
                    </span>
                )}
                <span
                    className={`hidden sm:inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium tracking-tight shrink-0 ${
                        isAdmin
                            ? "bg-slate-900 text-white"
                            : isManager
                            ? "bg-[#2EB4AC]/10 text-[#1F7F79]"
                            : "bg-slate-100 text-slate-600"
                    }`}
                >
                    {titleText}
                </span>
                {data.sub_department && (
                    <span className="hidden md:inline text-[11px] text-slate-400 truncate" data-testid={`org-sub-${data.user_id}`}>
                        · {data.sub_department}
                    </span>
                )}
                {data.date_of_joining && (
                    <span className="hidden md:inline-flex items-center gap-1 text-[11px] text-slate-500 whitespace-nowrap shrink-0" title={`Joined ${formatDoj(data.date_of_joining)}`} data-testid={`org-doj-${data.user_id}`}>
                        · Joined {formatDoj(data.date_of_joining)} · <span className="font-semibold text-amber-700">{tenureOf(data.date_of_joining)}</span>
                    </span>
                )}
            </button>

            {hasChildren && (
                <span
                    className="text-[10px] text-slate-400 tabular-nums shrink-0"
                    title={`${data.children.length} direct report${data.children.length !== 1 ? "s" : ""}`}
                >
                    {data.children.length}
                </span>
            )}

            {/* Hover-only "Promote to top-level" — explicit alternative to
                drop-into-empty-space, which can be fiddly. */}
            {canEdit && data.manager_id && (
                <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); promoteToRoot?.(data.user_id); }}
                    data-testid={`org-promote-${data.user_id}`}
                    title="Promote to top-level"
                    className="opacity-0 group-hover:opacity-100 transition-opacity h-7 w-7 rounded-md border border-slate-200 bg-white hover:border-[#2EB4AC] hover:text-[#2EB4AC] flex items-center justify-center text-slate-500"
                >
                    <ArrowUp className="h-3.5 w-3.5" />
                </button>
            )}
        </div>
    );
};

const flattenIds = (nodes) => {
    const out = [];
    const walk = (arr) => {
        for (const n of arr) {
            out.push(n.user_id);
            if (n.children?.length) walk(n.children);
        }
    };
    walk(nodes || []);
    return out;
};

const OrgChart = () => {
    const { user } = useAuth();
    const canEdit = user?.role === "super_admin";
    const [tree, setTree] = useState([]);
    const [loading, setLoading] = useState(true);
    const [query, setQuery] = useState("");
    const [viewMode, setViewMode] = useState("tree");  // "list" | "tree"
    // Bumping `treeKey` forces react-arborist to fully remount after a move so
    // it can never end up holding a stale dragged-node phantom in its internal
    // state — that was the root cause of the "drop-into-empty-space vanishes
    // the entry" report.
    const [treeKey, setTreeKey] = useState(0);
    const treeRef = useRef(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/org/tree");
            // react-arborist wants each node to have an `id` key.
            const tag = (nodes) =>
                nodes.map((n) => ({ ...n, id: n.user_id, children: n.children?.length ? tag(n.children) : [] }));
            setTree(tag(r.data?.tree || []));
        } catch (e) {
            toast.error("Failed to load org chart");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const handleMove = async ({ dragIds, parentId }) => {
        if (!canEdit) return;
        // parentId is `null` when dropped at root level in react-arborist. Force
        // an explicit null so the API call sets manager_id to null in Mongo
        // (avoids any "undefined" leakage from older library versions).
        const newParent = parentId === undefined ? null : parentId;
        for (const dragId of dragIds) {
            try {
                await api.put(`/users/${dragId}/move`, { new_manager_id: newParent });
                toast.success(newParent ? "Moved" : "Promoted to top-level");
            } catch (e) {
                toast.error(e?.response?.data?.detail || "Move failed");
                setTreeKey((k) => k + 1);
                load();
                return;
            }
        }
        // Always remount the tree after a successful move so the new root-drop
        // entry shows up correctly, even when react-arborist's internal state
        // diverged from our `data` prop during the drag.
        setTreeKey((k) => k + 1);
        load();
    };

    const promoteToRoot = async (userId) => {
        if (!canEdit) return;
        try {
            await api.put(`/users/${userId}/move`, { new_manager_id: null });
            toast.success("Promoted to top-level");
            setTreeKey((k) => k + 1);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Move failed");
        }
    };

    const expandAll = () => treeRef.current?.openAll?.();
    const collapseAll = () => treeRef.current?.closeAll?.();

    // Counts
    const flatIds = useMemo(() => flattenIds(tree), [tree]);
    const counts = { total: flatIds.length, roots: tree.length };

    // Default-open ONLY the top 2 levels so the first paint stays calm.
    // Anything below is collapsed; users opt in by clicking the chevron.
    const initialOpenState = useMemo(() => {
        const state = {};
        const walk = (nodes, depth) => {
            for (const n of nodes) {
                if (depth < 2) state[n.user_id] = true;
                if (n.children?.length) walk(n.children, depth + 1);
            }
        };
        walk(tree, 0);
        return state;
    }, [tree]);

    return (
        <div className="space-y-6" data-testid="org-chart-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Hierarchy</div>
                    <h1 className="font-heading text-4xl font-bold text-slate-900 tracking-tight mt-1 flex items-center gap-3">
                        <Network className="h-7 w-7 text-[#2EB4AC]" />
                        Org Chart
                    </h1>
                    <p className="text-slate-500 mt-1">
                        {canEdit
                            ? "Drag a row onto another to re-parent. Hover any row and click the up-arrow to promote them to top-level."
                            : "Browse the reporting structure across your team."}
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {/* View mode toggle */}
                    <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-slate-100" role="tablist" aria-label="View mode">
                        <button
                            onClick={() => setViewMode("list")}
                            data-testid="org-view-list"
                            aria-pressed={viewMode === "list"}
                            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium transition-colors ${
                                viewMode === "list" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
                            }`}
                        >
                            <ListIcon className="h-3.5 w-3.5" /> List
                        </button>
                        <button
                            onClick={() => setViewMode("tree")}
                            data-testid="org-view-tree"
                            aria-pressed={viewMode === "tree"}
                            className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium transition-colors ${
                                viewMode === "tree" ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"
                            }`}
                        >
                            <Network className="h-3.5 w-3.5" /> Org Tree
                        </button>
                    </div>

                    <div className="relative">
                        <SearchIcon className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        <input
                            type="text"
                            placeholder="Search by name, position…"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            data-testid="org-search"
                            className="h-10 w-64 pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-[#2EB4AC] focus:border-[#2EB4AC] outline-none transition"
                        />
                    </div>
                    {viewMode === "list" && (
                        <>
                            <button onClick={expandAll} data-testid="org-expand-all" className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-slate-200 hover:border-[#2EB4AC] hover:text-[#2EB4AC] text-sm font-medium transition-colors">
                                <ChevronsUpDown className="h-4 w-4" /> Expand
                            </button>
                            <button onClick={collapseAll} data-testid="org-collapse-all" className="inline-flex items-center gap-1.5 h-10 px-3 rounded-lg border border-slate-200 hover:border-[#2EB4AC] hover:text-[#2EB4AC] text-sm font-medium transition-colors">
                                <ChevronsDownUp className="h-4 w-4" /> Collapse
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* Stats strip */}
            <div className="flex flex-wrap items-center gap-3">
                <StatPill label="People" value={counts.total} icon={UserIcon} />
                <StatPill label="Top-level" value={counts.roots} icon={Network} />
                {canEdit && viewMode === "list" && (
                    <div className="inline-flex items-center gap-2 px-3 h-9 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 text-xs">
                        <Info className="h-3.5 w-3.5" /> Builder mode — drag to re-parent
                    </div>
                )}
                {viewMode === "tree" && (
                    <div className="inline-flex items-center gap-2 px-3 h-9 rounded-full bg-slate-100 text-slate-600 text-xs">
                        <Info className="h-3.5 w-3.5" /> Scroll horizontally to explore wide branches
                    </div>
                )}
            </div>

            {/* Tree */}
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-3">
                {loading ? (
                    <div className="space-y-2 p-3">
                        {Array.from({ length: 8 }).map((_, i) => (
                            <Skeleton key={i} className="h-10 w-full" />
                        ))}
                    </div>
                ) : tree.length === 0 ? (
                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-10 text-center">
                        <Network className="h-8 w-8 text-slate-400 mx-auto" />
                        <p className="mt-3 font-heading font-semibold text-slate-900">No reporting structure yet</p>
                        <p className="text-slate-500 text-sm mt-1">Once managers and reports are linked in Control Panel, the tree shows up here.</p>
                    </div>
                ) : viewMode === "list" ? (
                    <Tree
                        key={treeKey}
                        ref={treeRef}
                        data={tree}
                        openByDefault={false}
                        initialOpenState={initialOpenState}
                        rowHeight={48}
                        width="100%"
                        height={Math.min(720, Math.max(360, counts.total * 50 + 24))}
                        indent={36}
                        paddingTop={6}
                        paddingBottom={6}
                        searchTerm={query}
                        searchMatch={(node, term) =>
                            (node.data.name || "").toLowerCase().includes(term.toLowerCase()) ||
                            (node.data.position || "").toLowerCase().includes(term.toLowerCase()) ||
                            (node.data.email || "").toLowerCase().includes(term.toLowerCase())
                        }
                        disableDrag={!canEdit}
                        disableDrop={!canEdit}
                        onMove={handleMove}
                    >
                        {(props) => <Node {...props} canEdit={canEdit} promoteToRoot={promoteToRoot} currentUserId={user?.user_id} />}
                    </Tree>
                ) : (
                    <OrgTreeChart tree={tree} query={query} currentUserId={user?.user_id} />
                )}
            </div>
        </div>
    );
};

const StatPill = ({ label, value, icon: Icon }) => (
    <div className="inline-flex items-center gap-2 px-3 h-9 rounded-full bg-white border border-slate-200 text-sm text-slate-600">
        <Icon className="h-3.5 w-3.5 text-slate-400" />
        <span className="font-heading text-slate-900 font-semibold tabular-nums">{value}</span>
        <span>{label}</span>
    </div>
);

// ---------- Visual Org Tree (read-only) ----------
// Pure CSS hierarchical chart. Inspired by the classic "ul/li with pseudo-element
// connectors" pattern — but using flexbox for crisp alignment at every level.
// Filters via `query` so the user can spotlight a person across both views.

const flattenForFilter = (nodes) => {
    const out = [];
    const walk = (arr) => {
        for (const n of arr) {
            out.push(n);
            if (n.children?.length) walk(n.children);
        }
    };
    walk(nodes);
    return out;
};

const OrgTreeChart = ({ tree, query, currentUserId }) => {
    const term = (query || "").trim().toLowerCase();
    const matches = useMemo(() => {
        if (!term) return null;
        const all = flattenForFilter(tree);
        return new Set(
            all
                .filter(
                    (n) =>
                        (n.name || "").toLowerCase().includes(term) ||
                        (n.position || "").toLowerCase().includes(term) ||
                        (n.email || "").toLowerCase().includes(term)
                )
                .map((n) => n.user_id)
        );
    }, [tree, term]);

    return (
        <div className="overflow-x-auto overflow-y-hidden py-6 px-4">
            <div className="org-tree inline-flex">
                <ul>
                    {tree.map((n) => (
                        <OrgTreeNode key={n.user_id} node={n} matches={matches} currentUserId={currentUserId} />
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default OrgChart;
